import { FormEvent, useCallback, useContext, useEffect, useState } from 'react';
import {
  ILoanApplicationDetail,
  ILoanDocumentationUpdate,
} from '../../../../interface/LoanInterface';
import { AppContext } from '../../../../context/AppContext';
import { IAppState } from '../../../../interface/AppInterface';
import { NairaSymbol } from '../../../../utils/mappings';
import { formatDisplayDate, getDateParts } from '../../../../utils/dateTimeUtils';
import axios from 'axios';
import { endpoints } from '../../../../utils/URL';
import { IPharmacy } from '../../../../interface/PharmacyInterface';
import FileUpload from '../../../common/FileUpload';
import { isFormValidated } from '../../../../utils/formUtils';

const LoanInfo = ({ data, tabKey }: { data: ILoanApplicationDetail; tabKey: string }) => {
  const { appState }: { appState: IAppState } = useContext(AppContext);
  const [pharmacy, setPharmacy] = useState<IPharmacy | undefined>();
  const [fileUrl, setFileUrl] = useState<string>();
  const [acceptTerms, setAcceptTerms] = useState(false);
  const invalidStatus = ['Approved', 'Disbursed', 'Completed', 'Repaying'];
  const [loading, setLoading] = useState(false);

  //get rx details
  const getPharmacyDetails = useCallback(async () => {
    try {
      const url = `${endpoints.pharmacies.mainUrl}/${appState.pharmacyId}`;
      const response = await axios.get(url);
      const data = response.data as IPharmacy;
      setPharmacy(data);
    } catch (error) {
      console.error(error);
    }
  }, [appState.pharmacyId]);

  const uploadFile = (file: File, fileUrl: string) => {
    setFileUrl(fileUrl);
  };

  const deleteFile = (event: any) => {
    event?.preventDefault();
    setFileUrl('');
    let form = document.getElementById('loan-form') as HTMLFormElement;
    form.reset();
  };

  const updateApplicationAsync = async (event: FormEvent) => {
    if (!isFormValidated('loan-form')) {
      return;
    }

    event.preventDefault();

    const update: ILoanDocumentationUpdate = {
      isAcceptedTerms: true,
      signatureFile: fileUrl || '',
      applicationId: data.applicationId,
      additionalDocuments: [],
      agreementDate: new Date().toISOString(),
    };

    setLoading(true);
    try {
      const url = `${endpoints.loans.mainUrl}/applications`;
      const response = await axios.put(url, update);
      const data = response.data as IPharmacy;
      setPharmacy(data);

      //reset form
      let form = document.getElementById('loan-form') as HTMLFormElement;
      form.reset();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPharmacyDetails();
  }, [getPharmacyDetails]);

  return (
    <div className='my-5'>
      <section className='row justify-content-center mt-5'>
        <h5 className='col-sm-10'>Basic Info</h5>
        <div className='col-sm-10'>
          {data && (
            <div className='card prescription-card'>
              <div className='card-body'>
                <div className='row'>
                  <div className='col-sm-6 card-text prescription-card-text'>
                    <span>Loan Amount</span>:
                    <span className='ms-1 font-weight-bold'>
                      <span className='me-1'>{NairaSymbol}</span>
                      {data.principalApproved.toLocaleString()}
                    </span>
                  </div>
                  <div className='col-sm-6 card-text prescription-card-text'>
                    <span>Loan Tenor</span>:
                    <span className='ms-1 font-weight-bold'>{data.tenorApproved} months</span>
                  </div>
                </div>
                <div className='row mt-3'>
                  <div className='col-sm-6 card-text prescription-card-text'>
                    <span>Amount Repaid</span>:
                    <span className='ms-1 font-weight-bold'>
                      <span className='me-1'>{NairaSymbol}</span>
                      {data.amountRepaid.toLocaleString()}
                    </span>
                  </div>
                  <div className='col-sm-6 card-text prescription-card-text'>
                    <span>Amount Pending</span>:
                    <span className='ms-1 font-weight-bold'>
                      <span className='me-1'>{NairaSymbol}</span>
                      {data.amountPending.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className='col-sm-10 '>
          {/* show terms and conditions */}
          <form
            id='loan-form'
            className='form-group card prescription-card p-sm-5 p-4 fw-300'
            style={{ cursor: 'auto' }}
          >
            <fieldset disabled={invalidStatus.includes(data.applicationStatus) || loading}>
              <div className='my-3 text-center fs-24'>LOAN AGREEMENT</div>
              <div>
                This Loan Agreement (“Agreement”) is made and entered into on this
                <span className='mx-1 fw-800'>
                  {getDateParts(data.documentation.agreementDate).dayName}
                </span>
                day of
                <span className='mx-1 fw-800'>
                  {getDateParts(data.documentation.agreementDate).monthName}
                </span>
                <span className='mx-1 fw-800'>
                  {getDateParts(data.documentation.agreementDate).year}
                </span>
                between Wellahealth Technolgies and
                <span className='fw-800 mx-1'>
                  {appState.pharmacyName} of {pharmacy?.pharmacyAddress || ''}{' '}
                  {pharmacy?.stateOfPremise || ''}
                </span>
                <div className='mt-4 fw-400'>1. LOAN AMOUNT & DISBURSEMENT</div>
                <div>
                  The Lender agrees to loan the Borrower the principal sum of
                  <span className='fw-800 fs-20 text-danger mx-2 letter-spacing-2'>
                    {NairaSymbol}
                    {data.principalApproved.toLocaleString()}
                  </span>
                  (the “Loan”), disbursed on or about
                  <span className='mx-1 fw-800'>{getDateParts(data.dateDisbursed).dayName}</span>
                  day of
                  <span className='mx-1 fw-800'>{getDateParts(data.dateDisbursed).monthName}</span>
                  <span className='mx-1 fw-800'>{getDateParts(data.dateDisbursed).year}</span>
                  at an interest rate of
                  <span className='fw-800 ms-1'>3.5</span>% per month
                </div>
                <div className='mt-4 fw-400'>2. REPAYMENT TERMS</div>
                <div>
                  The Borrower agrees to repay the Loan by{' '}
                  <span className='mx-1 fw-800'>{getDateParts(data.repaymentDate).dayName}</span>
                  day of
                  <span className='mx-1 fw-800'>{getDateParts(data.repaymentDate).monthName}</span>
                  <span className='mx-1 fw-800'>{getDateParts(data.repaymentDate).year}</span> Early
                  repayment is allowed with interest only charged for period before which the loan
                  was fully repaid.Late payment attracts additional interest based on the period
                  repayment is delayed
                </div>
                <div className='mt-4 fw-400'>3. ENTIRE AGREEMENT</div>
                <div>
                  This Agreement shall be governed by and construed in accordance with the laws of
                  the Federal Republic of Nigeria
                </div>
                <div className='mt-4 fw-400'>4. GOVERNING LAW</div>
                <div>
                  This document reflects the entire agreement between the parties and supersedes all
                  prior agreements, whether written or oral. IN WITNESS WHEREOF, the parties have
                  executed this Agreement as of the date first written above.
                </div>
                <div className='mt-4 fw-500'>
                  Lender's Signature:
                  <br />
                  <span className='fw-800'>Wellahealth Technologies Limited</span>
                </div>
                <div className='fw-800'>
                  Date: {formatDisplayDate(data.documentation.agreementDate)}
                </div>
                <div className='mt-4 col-md-4'>
                  <div className='fw-500'>Borrower's Signature</div>
                  {data.documentation.signatureFile && (
                    <img
                      src={data.documentation.signatureFile}
                      alt='signature'
                      className='img-fluid'
                      style={{ maxWidth: '200px', maxHeight: '60px' }}
                    />
                  )}
                  {!data.documentation.signatureFile && !fileUrl && (
                    <div className=''>
                      <FileUpload
                        callback={uploadFile}
                        uploadToRemote={true}
                        allowedTypes={['.JPG', '.PNG']}
                        pharmacyCode={appState.pharmacyCode}
                      />
                    </div>
                  )}
                  {fileUrl && (
                    <div>
                      <img
                        src={fileUrl}
                        alt='signature'
                        className='img-fluid'
                        style={{ maxWidth: '200px', maxHeight: '60px' }}
                        // height={48}
                        // width={48}
                      />
                      <button className='btn btn-sm ms-4' title='delete' onClick={deleteFile}>
                        <span className='float-end bi bi-trash fs-16 text-danger'></span>
                      </button>
                    </div>
                  )}
                </div>
                <div className='fw-800 mt-2'>
                  Date: {formatDisplayDate(data.documentation.agreementDate)}
                </div>
                {!invalidStatus.includes(data.applicationStatus) && (
                  <div>
                    <div className='mt-4 fs-18 fw-700 form-check'>
                      <input
                        id='agree-check'
                        type='checkbox'
                        className='form-check-input'
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                        required
                      />
                      <label className='form-check-label' htmlFor='agree-check'>
                        I agree to the terms and conditions above.
                      </label>
                    </div>

                    <div className='mt-4 col-md-2 d-grid'>
                      <button
                        className='btn btn-primary'
                        type='submit'
                        onClick={updateApplicationAsync}
                      >
                        Submit
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </fieldset>
          </form>
        </div>
      </section>
    </div>
  );
};

export default LoanInfo;
